/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {PRODUCT_TILE_IMAGE_TYPE, TILE_CONTEXT_ACCESSORIES} from '../../constants'

// Components
import {AspectRatio, Box, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'

import DynamicImage from '../dynamic-image'
import SwatchGroup from '../swatch-group'
import Swatch from '../swatch-group/swatch'
import {trackProductTileClick} from '../../tracking/analytics'
import BadgeGroup from '../badge-group'
import Link from '../link'
import {ProductQuickviewModal, useProductQuickviewModal} from '../product-quickview-modal'

// Hooks
import {FormattedMessage, useIntl} from 'react-intl'

// Other
import {productUrlBuilder} from '../../utils/url'
import {useColorVariation} from '../../hooks'
import {useProductDisplayImages} from '../../hooks/use-product-display-images'
import {
    getPreferedVariant,
    isProductMaster,
    isProductSet,
    isProductVariant
} from '../../utils/product-utils'
import {useProductSetPrice} from '../../hooks/useScapiCustomApi'
import {isServer} from '../../utils/utils'
import AddToCartCTA from '../add-to-cart-cta'
import CustomizeCTA from './partials/customize-cta'
import RemoveFromWishlistOverlayButton from './partials/remove-from-wishlist-overlay-button'
import WishlistOverlayButton from './partials/wishlist-overlay-button'
import {ProductTileStyleProvider} from './context'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {DEFAULT_GET_PRODUCT_PRICE_PARAMETERS} from '../../hooks/useProductDetail'
import useEffectEvent from '../../hooks/use-effect-event'
import {CloseIcon, UnavailableCartIcon} from '../icons'
import ProductDisplayPrice from '../product-display-price'
import ColorSwatch from '../icons/colorSwatch'
import ProductTileLeapView from './partials/leap-view'
import LeapDisplayPrice from '../../leap/components/display-price'
import useMultiSite from '../../hooks/use-multi-site'
import LeapWishlistOverlayButton from './partials/leap-wishlist-overlay-button'
import {Link as ReactRouterLink} from 'react-router-dom'
import PayLater from '../pay-later'
import usePayLater from '../pay-later/usePayLater'
import useProductPricing from '../../hooks/use-product-pricing'
import {useCurrency} from '../../hooks'
import useHydration from '../../contexts/hydration'

export {default as Skeleton} from './partials/skeleton'

/**
 * The ProductTile is a visual representation of a product object.
 * Leap design variants (matching `/leap/`) render using `ProductTileLeapView`.
 *
 * Default handling is:
 * * click on tile opens QuickViewModal with tracking when `context` is
 *   `TILE_CONTEXT_ACCESSORIES`
 * * hover on tile shows second image if available
 * * click on color variation changes the displayed product variant (only for `default`, `reco` and `leap-reco` variants)
 * * click on wishlist overlay button adds/removes product from wishlist (only for `basic`, `default`, `reco`, `leap-reco` and `wishlist` variants)
 * * click on add to cart cta button adds product to cart (only for `wishlist` variant)
 * * click on more colors opens the product detail page in a new tab (only for `leap-reco` variant)
 *
 * @param {string} props.background - for leap variants: recipe variant (e.g., 'subtle') that adds
 *   background styling, padding and border radius; for non-leap variants: CSS background value
 * @param {string} props.context - context where the product tile is used - used for tracking.
 *   Do not extend the list of contexts - rather use `onClick` handler
 * @param {object} props.dynamicImageProps - `imageProps` for DynamicImage component
 * @param {object} props.product - the product data that will be represented in this component
 * @param {boolean} props.propagateDisabled - true to disable selecting unorderable variants
 * @param {boolean} props.reverseImageOrder - true to show the second
 *   image initially and the first image on hover (default is the other way around)
 * @param {'none'|'klarna'|'paypal'} props.payLaterVariant - pay later provider requested by the CMS.
 *   The site preferences (`enablePaypalLater`/`klarnaLaterEnabled`) always win: a provider that is
 *   disabled there never renders, no matter what is selected here
 * @param {'basic'|'default'|'order'|'reco'|'wishlist'|'leap-reco'} props.variant - the variant of the product tile:
 * - `basic`: images, product name, tags and add/remove from wishlist overlay button
 * - `default`: images, product name, product price, color variations and add/remove from wishlist overlay button
 * - `order`: image and product name
 * - `reco`: images, product name, product price, color variations and add/remove from wishlist overlay button
 * - `wishlist`: images, product name, product price, current color variation, remove from wishlist overlay button
 *   and add to cart cta button
 * - 'leap-reco': leap design based with images, product name, product price, color variations with more colors link to pdp
 *   and add/remove from wishlist overlay button
 * @param {function(product: object, event: object): boolean|void} props.onClick - click on tile. If it returns false,
 *   the default behavior is prevented
 * @param {function(product: object, event: object): boolean|void} props.onRecommendedProductClick -
 *   click on recommended product from add to cart modal
 *
 */
const ProductTile = forwardRef((props, ref) => {
    const {
        locale: {id: locale},
        buildUrl
    } = useMultiSite()
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('ProductTile', props)
    const finalFocusRef = useRef(null)

    const {
        background,
        context,
        dynamicImageProps,
        isDisabled,
        product: initialProduct,
        propagateDisabled,
        reverseImageOrder,
        payLaterVariant = 'none',
        titleProps,
        variant = 'default',
        onClick: _onClick,
        onRecommendedProductClick: _onRecommendedProductClick,
        onOverlayCtaClick,
        ...rest
    } = props

    const isLeapVariant = /leap/.test(variant)
    const isHydrated = useHydration()

    const quickViewModalProps = useProductQuickviewModal()
    const onRecommendedProductClick = _onRecommendedProductClick || quickViewModalProps.onOpen
    quickViewModalProps.onRecommendedProductClick = onRecommendedProductClick

    // this is an internal attribute added by wishlist and order details when the product does not exist
    const isProductDiscontinued = initialProduct?.productExists === false
    const isProductActive =
        !(isProductMaster(initialProduct) || isProductVariant(initialProduct)) ||
        initialProduct?.variants?.length > 0

    // selected product id
    const [_selectedProductId, setSelectedProductId] = useState(null)
    // in case the initial product changes we might have an old selected product id
    const selectedProductId = useMemo(() => {
        if (
            _selectedProductId &&
            initialProduct?.variants?.find((variant) => variant.productId === _selectedProductId)
        ) {
            return _selectedProductId
        }

        if (isProductVariant(initialProduct)) {
            return initialProduct.id
        }
        if (isProductMaster(initialProduct)) {
            return getPreferedVariant(initialProduct)?.productId
        }
        return initialProduct.id
    }, [initialProduct, _selectedProductId])
    // clear selected product id if the initial product changes
    useEffect(() => {
        setSelectedProductId(null)
    }, [initialProduct])

    // computed product variant or the initial product if no variants
    const variantProduct = useMemo(() => {
        if (selectedProductId === initialProduct.id) {
            return initialProduct
        }
        const variant = initialProduct.variants?.find(
            (variant) => variant.productId === selectedProductId
        )
        if (!variant) {
            return initialProduct
        }
        const {productId, ...rest} = variant
        return {
            ...initialProduct,
            ...rest,
            name: initialProduct.name,
            id: productId
        }
    }, [initialProduct, selectedProductId])

    const {
        CTAAction,
        ctaActionProps,
        OverlayAction,
        showBadges,
        showPrice,
        showVariations,
        showCustomizable
    } = useMemo(
        () =>
            ({
                basic: {
                    OverlayAction: isProductActive ? WishlistOverlayButton : null,
                    showBadges: !isProductDiscontinued,
                    showPrice: !isProductDiscontinued
                },
                default: {
                    OverlayAction: isProductActive ? WishlistOverlayButton : null,
                    showBadges: !isProductDiscontinued,
                    showPrice: !isProductDiscontinued,
                    showVariations: isProductActive && !isProductDiscontinued,
                    showCustomizable: isProductSet(initialProduct)
                },
                order: {},
                reco: {
                    OverlayAction: isProductActive ? WishlistOverlayButton : null,
                    showBadges: !isProductDiscontinued,
                    showPrice: !isProductDiscontinued,
                    showVariations: isProductActive && !isProductDiscontinued,
                    showCustomizable: isProductSet(initialProduct)
                },
                'leap-reco': {
                    OverlayAction: isProductActive ? LeapWishlistOverlayButton : null,
                    showBadges: !isProductDiscontinued,
                    showPrice: !isProductDiscontinued,
                    showVariations: isProductActive && !isProductDiscontinued,
                    showCustomizable: isProductSet(initialProduct)
                },
                wishlist: {
                    ...(!isProductActive || isProductDiscontinued
                        ? null
                        : isProductSet(initialProduct)
                        ? {
                              CTAAction: CustomizeCTA,
                              ctaActionProps: {
                                  isDisabled
                              }
                          }
                        : {
                              CTAAction: AddToCartCTA,
                              ctaActionProps: {
                                  isDisabled,
                                  onClick: (e) => {
                                      e?.preventDefault?.()
                                      e?.stopPropagation?.()
                                  },
                                  onRecommendedProductClick: onRecommendedProductClick
                              }
                          }),
                    OverlayAction: RemoveFromWishlistOverlayButton,
                    showPrice: !isProductDiscontinued,
                    showVariations: isProductActive && !isProductDiscontinued
                }
            }[variant] || {}),
        [
            initialProduct,
            isDisabled,
            isProductActive,
            isProductDiscontinued,
            variant,
            onRecommendedProductClick
        ]
    )

    // load variant promotions
    // variant promotions do not appear when loading master product set with getProduct, so we need to fetch it for the selected variant
    // for non-master products (variants, standalone) we also fetch if price data is missing
    const needsFetchingVariantPromotions =
        Boolean(selectedProductId) &&
        !isProductSet(initialProduct) &&
        (isProductMaster(initialProduct) || initialProduct.price == null)

    const {data: variantPromotionsData, isInitialLoading: isVariantPromotionsLoading} = useProduct(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCT_PRICE_PARAMETERS,
                id: selectedProductId
            }
        },
        {
            // fetch only on client to include customer prices
            enabled: needsFetchingVariantPromotions && !isServer
        }
    )

    // load set prices
    // promotions do not appear when loading product set with getProduct, so we need to fetch it in any case
    const needsFetchingCustomerPrice = Boolean(initialProduct?.id) && isProductSet(initialProduct)
    const {data: productSetPricesData} = useProductSetPrice(
        {
            parameters: {
                locale,
                c_pid: initialProduct?.id
            }
        },
        {
            // fetch only on client to include customer prices
            enabled: needsFetchingCustomerPrice && !isServer
        }
    )
    // server never fetches - keep the placeholder through hydration to match its markup
    const isPriceLoading =
        (needsFetchingCustomerPrice && !productSetPricesData?.resp) ||
        (needsFetchingVariantPromotions && (!isHydrated || isVariantPromotionsLoading))

    const productWithPrice = useMemo(() => {
        if (needsFetchingCustomerPrice) {
            return {
                ...variantProduct,
                ...productSetPricesData?.resp
            }
        }
        if (needsFetchingVariantPromotions) {
            return {
                ...variantProduct,
                ...variantPromotionsData
            }
        }
        return variantProduct
    }, [
        needsFetchingCustomerPrice,
        needsFetchingVariantPromotions,
        productSetPricesData,
        variantProduct,
        variantPromotionsData
    ])

    const {currency: activeCurrency} = useCurrency()
    const {currency: productCurrency, displayPrice} = useProductPricing(productWithPrice) || {}
    const currency = productCurrency || activeCurrency

    // The CMS only selects which provider is wanted here - whether it actually renders is decided
    // by the site preferences inside `PayLater`, which always win over this prop.
    const payLaterProps = useMemo(
        () => ({
            paypalPlacement: payLaterVariant === 'paypal' ? 'category' : undefined,
            klarnaPlacement: payLaterVariant === 'klarna' ? 'credit-promotion-badge' : undefined
        }),
        [payLaterVariant]
    )
    const {isPaypalEnabled, isKlarnaEnabled} = usePayLater({
        amount: displayPrice,
        currency,
        ...payLaterProps
    })
    // Only the provider the CMS asked for may decide this - an enabled Paypal must not stand in
    // for a disabled Klarna, that would mount Klarna's placement and its stuck loading animation.
    const showPayLater =
        {paypal: isPaypalEnabled, klarna: isKlarnaEnabled}[payLaterVariant] ?? false

    /**
     * Filter [imageGroups] by PRODUCT_TILE_IMAGE_TYPE and selected color variation.
     * Slice them because we need only first and second image
     */
    const images = useProductDisplayImages(
        productWithPrice,
        PRODUCT_TILE_IMAGE_TYPE,
        productWithPrice?.variationValues,
        2
    )

    /* Get extended variation attributes data with images etc. */
    const colorVariations = useColorVariation(productWithPrice)

    const changeColorVariation = (value) => {
        const variant = productWithPrice?.variants?.find(
            (variant) => variant.variationValues.color === value
        )

        if (variant?.productId) {
            setSelectedProductId(variant.productId)
        }
    }

    const onLeapColorSwatchesChange = useCallback(({value: productId}) => {
        setSelectedProductId(productId)
    }, [])

    const onClick = (e) => {
        if (isDisabled) {
            e.preventDefault()
            return
        }

        if (_onClick?.(productWithPrice, e) === false) {
            return
        }

        switch (context) {
            case TILE_CONTEXT_ACCESSORIES:
                trackProductTileClick(context, productWithPrice.id)
                break
            default:
                break
        }

        if (isProductSet(productWithPrice)) {
            // leave the default behavior for product sets
            return
        }

        e.preventDefault()
        finalFocusRef.current = e.currentTarget
        if (onRecommendedProductClick) {
            onRecommendedProductClick(productWithPrice.id, context, {
                finalFocusRef
            })
        } else {
            quickViewModalProps.onOpen(productWithPrice.id, context, {
                finalFocusRef
            })
        }
    }

    // This complex logic is needed to close the quick view modal after the product is added to the cart and add to cart modal is shown
    const [addedToCart, setAddedToCart] = useState(false)
    const onAfterAddToCart = useCallback(() => {
        setAddedToCart(true)
    }, [])
    const onAddedToCart = useEffectEvent(() => {
        quickViewModalProps.onClose()
    })
    useEffect(() => {
        if (addedToCart) {
            setAddedToCart(false)
            onAddedToCart()
        }
    }, [addedToCart, onAddedToCart])

    const productType = initialProduct['c_product-type']
    const productUrl = useMemo(
        () => productUrlBuilder(productWithPrice, locale),
        [productWithPrice, locale]
    )
    const fullProductUrl = buildUrl(productUrl)
    const {
        component: RootComponent,
        props: rootComponentProps,
        externalLinkProps
    } = isProductDiscontinued || productType === 'Engraving'
        ? isLeapVariant
            ? {
                  externalLinkProps: {display: 'none'}
              }
            : {
                  // Discontinued product or engraving
                  component: Box,
                  props: {
                      ...styles.container,
                      ...rest,
                      ...(background ?? {background})
                  },
                  externalLinkProps: {display: 'none'}
              }
        : isLeapVariant
        ? {
              props: {
                  as: ReactRouterLink,
                  role: 'button',
                  tabIndex: '0',
                  to: fullProductUrl,
                  'aria-disabled': isDisabled,
                  onClick
              },
              externalLinkProps: isDisabled
                  ? {display: 'none'}
                  : {
                        as: 'a',
                        href: fullProductUrl,
                        target: '_blank',
                        rel: 'noopener noreferrer'
                    }
          }
        : {
              // Active product
              component: Link,
              props: {
                  variant: 'image',
                  cursor: 'pointer',
                  role: 'button',
                  tabIndex: '0',
                  ...styles.container,
                  to: productUrl,
                  'aria-disabled': isDisabled,
                  onClick,
                  ...rest,
                  ...(background ?? {background}),
                  ref
              },
              externalLinkProps: isDisabled
                  ? {display: 'none'}
                  : {
                        as: 'a',
                        href: fullProductUrl,
                        target: '_blank',
                        rel: 'noopener noreferrer'
                    }
          }

    if (isLeapVariant) {
        return (
            <ProductTileLeapView.Root background={background} disabled={isDisabled}>
                <ProductTileLeapView.Container {...rootComponentProps}>
                    <ProductTileLeapView.Images
                        images={images}
                        widths={dynamicImageProps?.widths}
                        reverseImageOrder={reverseImageOrder}
                    />
                    {isProductDiscontinued && <ProductTileLeapView.Unavailable />}
                    {showBadges && !isPriceLoading && (
                        <ProductTileLeapView.Badges>
                            <BadgeGroup product={productWithPrice} max={2} maxSummedChars={18} />
                        </ProductTileLeapView.Badges>
                    )}
                    <ProductTileLeapView.ProductInfo>
                        <ProductTileLeapView.ProductDetails>
                            {showPayLater && (
                                <ProductTileLeapView.PayLater>
                                    <PayLater
                                        amount={displayPrice}
                                        currency={currency}
                                        {...payLaterProps}
                                    />
                                </ProductTileLeapView.PayLater>
                            )}
                            <ProductTileLeapView.ProductName>
                                {productWithPrice.c_masterName || productWithPrice.name}
                            </ProductTileLeapView.ProductName>
                            {showPrice && (
                                <ProductTileLeapView.Price>
                                    <ProductDisplayPrice
                                        as={LeapDisplayPrice}
                                        product={productWithPrice}
                                        loading={isPriceLoading}
                                        size="sm"
                                        data-testid="product-tile-price"
                                    />
                                </ProductTileLeapView.Price>
                            )}
                        </ProductTileLeapView.ProductDetails>
                        {showCustomizable && <ProductTileLeapView.Customize />}
                    </ProductTileLeapView.ProductInfo>
                </ProductTileLeapView.Container>
                {showVariations && colorVariations?.values?.length > 0 && (
                    <ProductTileLeapView.Swatches
                        value={productWithPrice?.id}
                        onValueChange={onLeapColorSwatchesChange}
                    >
                        {colorVariations.values.map((swatch) => (
                            <ProductTileLeapView.Swatch key={swatch.value} swatch={swatch} />
                        ))}
                        <ProductTileLeapView.SwatchesMoreButton
                            {...externalLinkProps}
                            count={colorVariations.values.length}
                        />
                    </ProductTileLeapView.Swatches>
                )}
                {OverlayAction && (
                    <ProductTileLeapView.OverlayActionContainer>
                        <OverlayAction
                            isDisabled={isDisabled}
                            product={productWithPrice}
                            onClick={onOverlayCtaClick}
                        />
                    </ProductTileLeapView.OverlayActionContainer>
                )}
                <ProductQuickviewModal
                    {...quickViewModalProps}
                    onAfterAddToCart={onAfterAddToCart}
                    showAddToCartModal={false}
                    scrollToTop={false}
                />
            </ProductTileLeapView.Root>
        )
    }

    return (
        <ProductTileStyleProvider value={styles}>
            <Box
                position="relative"
                data-testid="product-tile"
                role="group"
                aria-label={formatMessage({
                    id: 'global.product',
                    defaultMessage: 'Product'
                })}
            >
                <RootComponent {...rootComponentProps}>
                    {/* Image */}
                    <AspectRatio ratio={7 / 8}>
                        <Box {...styles.imageContainer}>
                            {images.length > 0 ? (
                                <>
                                    <DynamicImage
                                        {...styles.image}
                                        {...(images.length > 1 && {
                                            'data-not-hovered': 'container'
                                        })}
                                        src={`${
                                            images[0].disBaseLink || images[0].link
                                        }[?sw={width}&q=60]`}
                                        widths={dynamicImageProps?.widths}
                                        imageProps={{
                                            alt: images[0].alt,
                                            loading: 'lazy',
                                            objectFit: 'contain',
                                            cursor: 'inherit',
                                            ...dynamicImageProps?.imageProps
                                        }}
                                    />
                                    {images.length > 1 && (
                                        <DynamicImage
                                            {...styles.image}
                                            data-hovered="container"
                                            src={`${
                                                images[1].disBaseLink || images[1].link
                                            }[?sw={width}&q=60]`}
                                            widths={dynamicImageProps?.widths}
                                            imageProps={{
                                                alt: images[1].alt,
                                                loading: 'lazy',
                                                objectFit: 'contain',
                                                cursor: 'inherit',
                                                ...dynamicImageProps?.imageProps
                                            }}
                                        />
                                    )}
                                    <Box
                                        data-not-hovered={images.length > 1 && 'container'}
                                        {...styles.imageOverlay}
                                    />
                                </>
                            ) : (
                                <Flex {...styles.unavailableImageContainer}>
                                    <Box {...styles.unavailableIconBox}>
                                        <UnavailableCartIcon boxSize={6} />
                                        <Box {...styles.closeIconRound}>
                                            <CloseIcon color={'white'} boxSize={1.5} />
                                        </Box>
                                    </Box>
                                    <Text {...styles.unavailableText}>
                                        <FormattedMessage
                                            defaultMessage="This product is no longer available."
                                            id="product_tile.discontinued"
                                        />
                                    </Text>
                                </Flex>
                            )}

                            {showBadges && !isPriceLoading && (
                                <BadgeGroup
                                    containerProps={styles.badgesContainer}
                                    product={productWithPrice}
                                    max={2}
                                    maxSummedChars={18}
                                />
                            )}
                        </Box>
                    </AspectRatio>

                    <Flex {...styles.infoContainer}>
                        <Flex {...styles.primaryInfoContainer}>
                            {/* Title */}
                            <Text {...styles.title} {...titleProps}>
                                {productWithPrice.c_masterName || productWithPrice.name}
                            </Text>

                            {/* Price */}
                            {showPrice && (
                                <ProductDisplayPrice
                                    product={productWithPrice}
                                    loading={isPriceLoading}
                                    variant="productTile"
                                    data-testid="product-tile-price"
                                />
                            )}
                        </Flex>
                    </Flex>
                    {/*customizable if its a set*/}
                    {showCustomizable && (
                        <Flex alignItems="center">
                            <ColorSwatch width="1.5rem" height="1.5rem" />
                            <Text
                                textStyle="Product Tile"
                                color="stokkePink.dark"
                                pl={1}
                                data-testid="producttile-customize"
                            >
                                <FormattedMessage
                                    defaultMessage="Customise"
                                    id="product_tile.customize"
                                />
                            </Text>
                        </Flex>
                    )}
                </RootComponent>

                <Box
                    width="100%"
                    position="absolute"
                    top={0}
                    aspectRatio="1/1.15"
                    pointerEvents="none"
                >
                    {OverlayAction && (
                        <OverlayAction
                            isDisabled={isDisabled}
                            product={productWithPrice}
                            onClick={onOverlayCtaClick}
                        />
                    )}
                </Box>

                {/* Color Variations */}
                {showVariations && colorVariations?.values?.length > 0 && (
                    <SwatchGroup
                        showLabel={false}
                        spacing={1}
                        size="sm"
                        {...styles.variations}
                        label={colorVariations.name}
                        value={
                            productWithPrice?.variationValues?.color ||
                            productWithPrice?.variationAttributes?.[0].values?.[0].value
                        }
                        onChange={(value) => {
                            changeColorVariation(value)
                        }}
                    >
                        {colorVariations.values.map(({href, name, image, value, orderable}) => (
                            <Swatch
                                key={value}
                                href={href}
                                disabled={!orderable}
                                value={value}
                                name={
                                    orderable
                                        ? name
                                        : formatMessage(
                                              {
                                                  id: 'product_tile.variant_name.accessibility.out_of_stock',
                                                  defaultMessage: '{colorName} - out of stock'
                                              },
                                              {colorName: name}
                                          )
                                }
                                size="sm"
                                propagateDisabled={propagateDisabled}
                                tooltipPlacement="top"
                                {...styles.variationSwatch}
                            >
                                {image ? (
                                    <Box
                                        height="100%"
                                        width="100%"
                                        borderRadius="full"
                                        backgroundRepeat="no-repeat"
                                        backgroundSize="cover"
                                        backgroundColor={name.toLowerCase()}
                                        backgroundImage={
                                            image
                                                ? `url("${encodeURI(
                                                      image.disBaseLink || image.link
                                                  )}")`
                                                : ''
                                        }
                                        {...styles.variationImage}
                                    />
                                ) : (
                                    <Text {...styles.variationText}>{name.charAt(0)}</Text>
                                )}
                            </Swatch>
                        ))}
                    </SwatchGroup>
                )}

                {/* CTA Action */}
                {CTAAction && (
                    <CTAAction
                        {...styles.ctaAction}
                        product={productWithPrice}
                        {...ctaActionProps}
                    />
                )}
            </Box>

            <ProductQuickviewModal
                {...quickViewModalProps}
                onAfterAddToCart={onAfterAddToCart}
                showAddToCartModal={false}
                scrollToTop={false}
            />
        </ProductTileStyleProvider>
    )
})

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = {
    background: PropTypes.string,
    // tracking related context
    context: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    isDisabled: PropTypes.bool,
    /**
     * The product data that will be represented in this component.
     */
    product: PropTypes.object,
    // true to disable selecting unorderable variants
    propagateDisabled: PropTypes.bool,
    reverseImageOrder: PropTypes.bool,
    // pay later provider requested by the CMS - site preferences still decide if it renders
    payLaterVariant: PropTypes.oneOf(['none', 'klarna', 'paypal']),
    // addition props for the title text
    titleProps: PropTypes.object,
    variant: PropTypes.string,
    // click on tile
    onClick: PropTypes.func,
    onCTAClick: PropTypes.func,
    onOverlayCtaClick: PropTypes.func,
    // click on recommended product on add to cart modal
    onRecommendedProductClick: PropTypes.func
}

export default ProductTile
